import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  CButton,
  CBadge,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormInput,
  CFormSelect,
  CFormText,
  CRow,
  CSpinner,
} from "@coreui/react";

import { apiClient } from "services/Api";
import ToastNotification from "components/globals/ToastNotification";
import { triggerAlert } from "components/globals/AlertNotification";

const scopeOptions = ["Nacional", "Comunitário", "Internacional"];

const IntellectualProperty = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const isViewOnly = new URLSearchParams(location.search).get("view") === "1";

  const fetchItemDetails = async () => {
    try {
      const response = await apiClient.get(`/intellectual-properties/${id}/`);
      setFormData(response.data);
    } catch (error) {
      triggerAlert("Propriedade intelectual não encontrada!", "error");
      navigate("/propriedade-intelectual");
    }
  };

  useEffect(() => {
    if (id) {
      fetchItemDetails();
    } else {
      setFormData({
        type: "",
        designation: "",
        scope: "",
        registration_date: "",
        expiration_date: "",
        active: true,
      });
    }
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value === "" ? null : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (id) {
        await apiClient.put(`/intellectual-properties/${id}/`, formData);
        triggerAlert(
          "Propriedade intelectual atualizada com sucesso!",
          "success",
        );
      } else {
        await apiClient.post("/intellectual-properties/", formData);
        triggerAlert("Propriedade intelectual criada com sucesso!", "success");
      }
      navigate("/propriedade-intelectual");
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Erro ao gravar propriedade intelectual";
      setToast({
        title: "Propriedade Intelectual",
        message: errorMessage,
        type: "error",
      });
      setLoading(false);
    }
  };

  if (!formData) {
    return (
      <div className="text-center">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (isViewOnly) {
    return (
      <div className="container mt-4">
        <CCard className="shadow-sm">
          <CCardHeader className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">Detalhes da Propriedade Intelectual</h4>
              <div className="text-medium-emphasis small">
                #{formData.id || "-"} • {formData.created_at || "-"}
              </div>
            </div>
            <div className="d-flex gap-2">
              <CButton
                color="info"
                className="text-white"
                onClick={() => navigate(`/propriedade-intelectual/${id}`)}
              >
                Editar
              </CButton>
              <CButton
                color="secondary"
                variant="outline"
                onClick={() => navigate("/propriedade-intelectual")}
              >
                Voltar
              </CButton>
            </div>
          </CCardHeader>
          <CCardBody className="d-flex flex-column gap-4">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <CBadge color={formData.active ? "success" : "secondary"}>
                {formData.active ? "Ativa" : "Inativa"}
              </CBadge>
              <div className="text-medium-emphasis small">
                {formData.created_by_name || "-"}
              </div>
            </div>

            <CRow className="g-3">
              <CCol md={6}>
                <div className="border rounded-3 p-3 bg-body-tertiary h-100">
                  <div className="text-uppercase text-medium-emphasis small">
                    Tipo
                  </div>
                  <div className="fw-semibold">{formData.type || "-"}</div>
                </div>
              </CCol>
              <CCol md={6}>
                <div className="border rounded-3 p-3 bg-body-tertiary h-100">
                  <div className="text-uppercase text-medium-emphasis small">
                    Abrangência
                  </div>
                  <div className="fw-semibold">{formData.scope || "-"}</div>
                </div>
              </CCol>
              <CCol md={12}>
                <div className="border rounded-3 p-3 bg-body-tertiary">
                  <div className="text-uppercase text-medium-emphasis small">
                    Designação
                  </div>
                  <div className="fw-semibold">
                    {formData.designation || "-"}
                  </div>
                </div>
              </CCol>
              <CCol md={6}>
                <div className="border rounded-3 p-3 bg-body-tertiary h-100">
                  <div className="text-uppercase text-medium-emphasis small">
                    Data de registo
                  </div>
                  <div className="fw-semibold">
                    {formData.registration_date || "-"}
                  </div>
                </div>
              </CCol>
              <CCol md={6}>
                <div className="border rounded-3 p-3 bg-body-tertiary h-100">
                  <div className="text-uppercase text-medium-emphasis small">
                    Prazo de validade/caducidade
                  </div>
                  <div className="fw-semibold">
                    {formData.expiration_date || "-"}
                  </div>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <ToastNotification toast={toast} setToast={setToast} />

      <CCard className="shadow-sm">
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <div>
            <h4 className="mb-1">
              {id
                ? "Editar Propriedade Intelectual"
                : "Nova Propriedade Intelectual"}
            </h4>
            <div className="text-medium-emphasis small">
              Registe a propriedade intelectual associada.
            </div>
          </div>
          <div className="d-flex gap-2">
            <CButton
              color="secondary"
              variant="outline"
              onClick={() => navigate("/propriedade-intelectual")}
              disabled={loading}
            >
              Cancelar
            </CButton>
            <CButton color="info" className="text-white" onClick={handleSubmit}>
              {loading ? "A guardar..." : "Guardar"}
            </CButton>
          </div>
        </CCardHeader>
        <CCardBody>
          <CForm onSubmit={handleSubmit}>
            <CFormText className="text-muted mb-4">
              Campos obrigatórios assinalados com *
            </CFormText>

            <div className="border rounded-3 p-3 bg-body-tertiary mb-4">
              <div className="text-uppercase text-medium-emphasis small mb-3">
                Identificação
              </div>
              <CRow className="g-3">
                <CCol md={6}>
                  <CFormInput
                    label="Tipo *"
                    name="type"
                    value={formData.type || ""}
                    onChange={handleInputChange}
                    required
                  />
                </CCol>
                <CCol md={6}>
                  <CFormInput
                    label="Designação *"
                    name="designation"
                    value={formData.designation || ""}
                    onChange={handleInputChange}
                    required
                  />
                </CCol>
                <CCol md={6}>
                  <CFormSelect
                    label="Abrangência *"
                    name="scope"
                    value={formData.scope || ""}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Selecione...</option>
                    {scopeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
                <CCol md={6}>
                  <CFormInput
                    type="date"
                    label="Data de registo *"
                    name="registration_date"
                    value={formData.registration_date || ""}
                    onChange={handleInputChange}
                    required
                  />
                </CCol>
                <CCol md={6}>
                  <CFormInput
                    type="date"
                    label="Prazo de validade/caducidade"
                    name="expiration_date"
                    value={formData.expiration_date || ""}
                    onChange={handleInputChange}
                  />
                </CCol>
                <CCol md={6}>
                  <CFormSelect
                    label="Ativa *"
                    name="active"
                    value={formData.active === true ? "true" : "false"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        active: e.target.value === "true",
                      }))
                    }
                    required
                  >
                    <option value="true">Sim</option>
                    <option value="false">Não</option>
                  </CFormSelect>
                </CCol>
              </CRow>
            </div>
          </CForm>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default IntellectualProperty;
